import { useEffect, useMemo, useState } from 'react';
import {
  COUNTRY_OPTIONS,
  getStateOptions,
  getCityOptions,
  ensureCitiesLoaded,
  citiesReady,
  withCurrentValue,
} from '../../data/locations';

/**
 * Cascading Country → State → City for any form.
 *
 * Returns three prop bundles to spread onto <SearchableSelect />. The page keeps
 * full control of layout, which is why this is a hook and not a fixed component
 * (Add Lead puts City next to Location, Add Project groups all three).
 *
 * onChange is called once per changed field with a synthetic
 * { target: { name, value } }, so it drops straight into existing
 * handleChange(e) handlers. Picking a country clears state + city; picking a
 * state clears city.
 *
 * @param {object}   p
 * @param {string}   p.country  current country value (a name, e.g. "India")
 * @param {string}   p.state    current state value
 * @param {string}   p.city     current city value
 * @param {Function} p.onChange (event) => void
 * @param {object}   [p.names]  field names, defaults to country/state/city
 * @param {object}   [p.errors] { country, state, city }
 */
export default function useLocationFields({
  country = '',
  state = '',
  city = '',
  onChange,
  names = {},
  errors = {},
}) {
  const countryName = names.country || 'country';
  const stateName = names.state || 'state';
  const cityName = names.city || 'city';

  // Flipped once the lazily-loaded city dataset lands, to trigger a re-render.
  const [cityDataVersion, setCityDataVersion] = useState(citiesReady() ? 1 : 0);
  const [cityLoading, setCityLoading] = useState(false);

  // Pull in the city dataset only once a country is actually picked.
  useEffect(() => {
    if (!country || citiesReady()) return;
    let alive = true;
    setCityLoading(true);
    ensureCitiesLoaded()
      .then(() => { if (alive) setCityDataVersion((v) => v + 1); })
      .catch(() => {})
      .finally(() => { if (alive) setCityLoading(false); });
    return () => { alive = false; };
  }, [country]);

  const stateOptions = useMemo(
    () => withCurrentValue(getStateOptions(country), state),
    [country, state]
  );

  const cityOptions = useMemo(
    () => withCurrentValue(getCityOptions(country, state), city),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [country, state, city, cityDataVersion]
  );

  const emit = (name, value) => onChange?.({ target: { name, value } });

  const handleCountry = (e) => {
    const next = e.target.value;
    if (next === country) return;
    emit(countryName, next);
    if (state) emit(stateName, '');
    if (city) emit(cityName, '');
  };

  const handleState = (e) => {
    const next = e.target.value;
    if (next === state) return;
    emit(stateName, next);
    if (city) emit(cityName, '');
  };

  const handleCity = (e) => emit(cityName, e.target.value);

  return {
    countryProps: {
      name: countryName,
      value: country,
      options: withCurrentValue(COUNTRY_OPTIONS, country),
      onChange: handleCountry,
      placeholder: 'Select Country',
      searchPlaceholder: 'Search country...',
      error: errors.country,
    },
    stateProps: {
      name: stateName,
      value: state,
      options: stateOptions,
      onChange: handleState,
      placeholder: country ? 'Select State' : 'Select a country first',
      searchPlaceholder: 'Search state...',
      disabled: !country,
      emptyMessage: 'No states for this country',
      error: errors.state,
    },
    cityProps: {
      name: cityName,
      value: city,
      options: cityOptions,
      onChange: handleCity,
      placeholder: country ? 'Select City' : 'Select a country first',
      searchPlaceholder: 'Search city...',
      disabled: !country,
      loading: cityLoading && !city,
      emptyMessage: cityLoading ? 'Loading cities...' : 'No cities found — type to add your own',
      allowCustom: true,
      error: errors.city,
    },
  };
}
